import get from 'lodash/get';
import isArray from 'lodash/isArray';
import includes from 'lodash/includes';
import some from 'lodash/some';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { AppsService as Apps } from '@/common/apps/apps.service';
import { MultiSelectService } from '@/common/multi-select/multi-select.service.es6';
import isUndefined from 'lodash/isUndefined';
import { PerSubscriptionUserStorage } from '@/utils/storage';
import { APP_PLATFORM_TYPES, isWebBasedApp, isMobileApp } from '@/utils/apps';
import { isPlatformApp } from '@pendo/middleware';
import { i18n_t } from '@utils/i18n';
import { canViewSessionReplayForApp } from '@/vuex/modules/auth/auth.helpers.js';
import { getActiveApplicationsMap } from '@/data/session/session-globals.ts';

const $t = i18n_t;

class AppChooser {
    constructor () {
        this.allAppsOption = [
            {
                id: '*',
                displayName: 'All Apps',
            },
        ];

        this.selected = this.allAppsOption;

        this.multiAppStorageKey = 'multi-app-chooser--storage-key';
    }

    loadOptions (platform, apps) {
        return apps.reduce((acc, app) => {
            const { platform: appPlatform, displayName, id, framework, integrated = false, faviconB64 = null, trainingAttributes, applicationFlags } = app;
            const disabled = platform && !platform.includes(appPlatform);
            if (!disabled) {
                acc.push({
                    id,
                    displayName,
                    platform: appPlatform,
                    framework,
                    integrated,
                    ...(faviconB64 && { faviconB64 }),
                    ...(trainingAttributes && { trainingAttributes }),
                    ...(applicationFlags && { applicationFlags }),
                });
            }

            return acc;
        }, []);
    }

    filterAppListByAppFilterOptions (apps, appFilterOptions) {
        const platforms = appFilterOptions.reduce((acc, option) => {
            if (!isPlatformApp(option.id)) return acc;

            acc.push(option.id);

            return acc;
        }, []);

        if (platforms.includes('*')) {
            return apps;
        }

        const shouldFilterByPlatform = platforms.length > 0;

        return apps.filter((app) => {
            if (shouldFilterByPlatform) {
                const matchesPlatformSelections = platforms.some((platform) => {
                    if (platform === 'mobile') {
                        return isMobileApp(app.platform);
                    }

                    return platform.includes(app.platform);
                });

                return matchesPlatformSelections;
            }

            const idMatchesFilter = appFilterOptions.some((option) => option.id === app.id);

            return idMatchesFilter;
        });
    }

    getAppsByPlatformType (platformId) {
        const apps = [];
        if (platformId.includes('*')) {
            apps.push(...store.getters['apps/list']);
        }
        if (platformId.includes('web')) {
            apps.push(...store.getters['apps/webList']);
        }
        if (platformId.includes('extension')) {
            apps.push(...store.getters['apps/extensionList']);
        }
        if (platformId.includes('mobile')) {
            apps.push(...store.getters['apps/mobileList']);
        }

        return apps;
    }

    /**
     * Returns IDs for each selected app. If a specific app is selected,
     * the ID for that app is returned. If a category of apps has been selected
     * (e.g. 'Mobile Apps'), an array will be returned containing the IDs of each
     * mobile app associated with the subscription.
     * @returns {Array|Number} A number or array containing IDs corresponding to the selected App(s)
     */
    getSelectedAppIds () {
        if (!store.getters['apps/isMultiApp']) return Promise.resolve([Apps.ALL_AGG_ID]);

        return this.loadSelected().then((apps) => {
            return apps.flatMap((app) => Apps.getAppAggIdsFromOption(app.id));
        });
    }

    /**
     * This code is written specifically as a bandaid for OneUI. We are not allowing
     * subscribers to see visitor activity/timeline data for extension apps. The
     * simplest way to make this happen is to expand out the appId being passed into
     * the aggregation on the frontend.
     * @returns {Array<Number>} An array of IDs corresponding to the selected App(s)
     */
    removeExtensionAppsFromAppIds (appFilter) {
        const appIds = isArray(appFilter) ? appFilter.flatMap((x) => x) : [appFilter];

        const apps = store.getters['apps/list'];

        return [
            ...new Set(
                appIds.flatMap((id) => {
                    switch (id) {
                        case 'expandAppIds("*")':
                            return apps
                                .filter((app) => app.platform !== APP_PLATFORM_TYPES.EXTENSION)
                                .map((app) => app.id);
                        case 'expandAppIds("ios")':
                            return apps.filter((app) => app.platform === APP_PLATFORM_TYPES.IOS).map((app) => app.id);
                        case 'expandAppIds("android")':
                            return apps
                                .filter((app) => app.platform === APP_PLATFORM_TYPES.ANDROID)
                                .map((app) => app.id);
                        case 'expandAppIds("mobile")':
                            return apps
                                .filter((app) =>
                                    [APP_PLATFORM_TYPES.IOS, APP_PLATFORM_TYPES.ANDROID].includes(app.platform),
                                )
                                .map((app) => app.id);
                        case 'expandAppIds("web")':
                            return apps.filter((app) => app.platform === APP_PLATFORM_TYPES.WEB).map((app) => app.id);
                        case 'expandAppIds("extension")':
                            return [];
                        default: {
                            const appMap = getActiveApplicationsMap();
                            const app = appMap[id];

                            if (app.platform === APP_PLATFORM_TYPES.EXTENSION) {
                                return [];
                            }

                            return id;
                        }

                    }
                }),
            ),
        ];
    }

    hasSelectedExtensionApps (appFilter) {
        const appIds = isArray(appFilter) ? appFilter.flatMap((x) => x) : [appFilter];
        const apps = store.getters['apps/list'];

        const selectedApps = [...new Set(Apps.expandAppIds(appIds, apps))];

        return selectedApps.length !== this.removeExtensionAppsFromAppIds(appIds).length;
    }

    getAppIds (app) {
        return Apps.getAppAggIdsFromOption(app.id);
    }

    /**
     * Returns a dropdown display name based on the provided appId, assuming it
     * maps to a default option. In the future, appId may be an array that does not
     * map to 'All Mobile', in which case these checks will have to be modified.
     * @returns {String} A default display name option for the App Chooser
     */
    getDefaultOptionFromAppId (appId) {
        return Apps.getAppGroupLabelByAggId(appId);
    }

    // weird function, but `app` can be {id: '*'} etc on dashboards and we need
    // to expand that out.
    loadPredefinedAppOption (app) {
        if (!app) return Promise.resolve(app);
        const displayName = this.getDefaultOptionFromAppId(app.id);
        // if displayName is falsy, then it must be a real app and not a "default option"
        if (displayName) {
            return Promise.resolve({
                ...app,
                displayName,
            });
        }

        const appMap = getActiveApplicationsMap();
        const realApp = appMap[app.id];

        return Promise.resolve({
            displayName: realApp.displayName,
            id: realApp.id,
            platform: realApp.platform,
            icon: Apps.getAppIconLabel(realApp.platform),
        });
    }

    getOptionByAppId (appId) {
        const displayName = this.getDefaultOptionFromAppId(appId);
        // if displayName is falsy, then it must be a real app and not a "default option"
        if (displayName) {
            return {
                id: appId,
                displayName,
            };
        }

        const appMap = getActiveApplicationsMap();
        const app = appMap[appId];

        return {
            displayName: app.displayName,
            id: app.id,
            platform: app.platform,
            icon: Apps.getAppIconLabel(app.platform),
            faviconB64: app.faviconB64,
        };
    }

    async loadSelected () {
        // get selected Apps from vuex store
        let globalSelectedApps = store.state.globalFilters.selectedApps;
        if (globalSelectedApps) return Promise.resolve(globalSelectedApps);

        // load selected Apps from vuex storage if they are not available on initial render
        await store.dispatch('globalFilters/loadSelectedApps');
        globalSelectedApps = store.state.globalFilters.selectedApps;
        if (globalSelectedApps) {
            this.selected = globalSelectedApps;

            return Promise.resolve(globalSelectedApps);
        }

        // otherwise, set selected apps to default All Apps option
        globalSelectedApps = this.allAppsOption;
        this.selected = globalSelectedApps;
        store.commit('globalFilters/setSelectedApps', {
            selectedApps: globalSelectedApps,
        });

        return Promise.resolve(globalSelectedApps);
    }

    setSelected (app) {
        this.selected = app;

        return this.saveSelected(app);
    }

    async saveSelected (apps) {
        store.commit('globalFilters/setSelectedApps', { selectedApps: apps });

        return PerSubscriptionUserStorage.set(this.multiAppStorageKey, apps);
    }

    filterBySelectedApp (collection, options = {}) {
        const {
            pathToAppId = ['appId'],
            appFilterId = [get(this.selected[0], 'id')],
            defaultAppId = Apps.legacyAppId,
        } = options;

        const propToCompare = this.getAssociatedAppProp(appFilterId[0]);

        if (propToCompare) {
            const appMap = getActiveApplicationsMap();

            return collection.filter((item) => {
                const itemAppId = [].concat(get(item, pathToAppId, defaultAppId));

                return some(itemAppId, (appId) => {
                    const app = appMap[appId];

                    if (!app) return false;

                    return appFilterId.includes(app[propToCompare]);
                });
            });
        }

        return collection;
    }

    getAssociatedAppProp (appFilterId = this.selected[0].id) {
        switch (appFilterId) {
            case Apps.ALL_PLATFORM:
                return null;
            case Apps.WEB_PLATFORM:
            case Apps.EXTENSION_PLATFORM:
            case Apps.IOS_PLATFORM:
            case Apps.ANDROID_PLATFORM:
                return 'platform';
            default:
                return 'id';
        }
    }

    isMobileOptionId (appFilterId) {
        if (appFilterId === 'mobile') {
            return true;
        }

        const appMap = getActiveApplicationsMap();
        const app = appMap[appFilterId];

        return !!app && includes(Apps.MOBILE_PLATFORMS, app.platform);
    }

    isGroupOption (id) {
        return includes([Apps.ALL_PLATFORM, Apps.WEB_PLATFORM, 'mobile'], id);
    }

    formatOptions ({ platform, excludeGroups, enabledMap, filterNonIntegratedApps, customOptions = [], formatOptions = {} } = {}) {
        const { excludePartnerAdoptApp = false, sessionReplay = false } = formatOptions;

        let appsList = customOptions.length > 0 ? customOptions : store.getters['apps/allWebPlusIntegratedMobileApps'];
        if (filterNonIntegratedApps) {
            if (customOptions.length > 0) {
                appsList = customOptions.filter((app) => app.integrated);
            } else {
                appsList = store.getters['apps/onlyIntegratedApps'];
            }
        }

        if (excludePartnerAdoptApp) {
            appsList = appsList.filter((app) => !get(app, 'applicationFlags.partnerAdoptApplication', false));
        }
        const appOptions = Apps.sortApps(appsList);

        const options = this.loadOptions(platform, appOptions);
        const unformattedOptions = [];

        if (!excludeGroups) {
            unformattedOptions.push({
                group: 'default',
                apps: this.getGroupOptions(platform, appOptions, formatOptions),
            });
        }

        unformattedOptions.push({
            ...(!excludeGroups && { group: 'divider' }),
            ...(excludeGroups && { group: 'default' }),
            apps: options.reduce((acc, option) => {
                if (!isUndefined(platform) && !platform.includes(option.platform)) {
                    return acc;
                }

                if (enabledMap && enabledMap[option.id] === false) {
                    return acc;
                }
                acc.push({
                    ...option,
                    ...(sessionReplay &&
                        !canViewSessionReplayForApp(option.id) && {
                        disabled: true,
                        tooltip: { content: 'You do not have access to replays for this app', arrow: true },
                    }),
                });

                return acc;
            }, []),
        });

        // 1.x component library handles group values much better. no need to map over and build them dynamically
        return MultiSelectService.formatOptions(unformattedOptions, false);
    }

    getGroupOptions (platform, appOptions, formatOptions = {}) {
        // Declared within function so that translation executes at runtime
        const GROUP_DISPLAY_NAMES = {
            '*': {
                noun: $t('All Apps'),
                verb: $t('Select all apps'),
            },
            'web|extension': {
                noun: $t('Web & Extension Apps'),
                verb: $t('Select web & extension apps'),
            },
            'web': {
                noun: $t('Web Apps'),
                verb: $t('Select web apps'),
            },
            'mobile': {
                noun: $t('Mobile Apps'),
                verb: $t('Select mobile apps'),
            },
            'extension': {
                noun: $t('Extension Apps'),
                verb: $t('Select extension apps'),
            },
        };

        const {
            includeComboWebAndExtensionOption = false,
            includeAllAppsOption = false,
            groupLabelType = 'noun',
            disableMobileOption = false,
        } = formatOptions;

        const hasMobileApps = some(appOptions, (app) => isMobileApp(app.platform));
        const hasExtensionApps = some(appOptions, (app) => app.platform === Apps.EXTENSION_PLATFORM);

        const platformIsWebBasedOrIncludesWebBased =
            !isUndefined(platform) && (isWebBasedApp(platform) || platform.includes(Apps.WEB_LIKE_PLATFORMS));

        return [
            {
                id: '*',
                $isDisabled: !includeAllAppsOption && !isUndefined(platform),
            },
            {
                id: 'web|extension',
                $isDisabled:
                    !includeComboWebAndExtensionOption ||
                    !hasExtensionApps ||
                    (!isUndefined(platform) && !platform.includes(Apps.WEB_LIKE_PLATFORMS)),
            },
            {
                id: 'web',
                $isDisabled: !isUndefined(platform) && !platform.includes(Apps.WEB_PLATFORM),
            },
            {
                id: 'mobile',
                $isDisabled: disableMobileOption || !hasMobileApps || (!isUndefined(platform) && platformIsWebBasedOrIncludesWebBased),
            },
            {
                id: 'extension',
                $isDisabled:
                    !hasExtensionApps || (!isUndefined(platform) && !platform.includes(Apps.EXTENSION_PLATFORM)),
            },
        ]
            .filter(({ $isDisabled }) => !$isDisabled)
            .map((item) => ({ ...item, displayName: GROUP_DISPLAY_NAMES[item.id][groupLabelType] }));
    }

    getAllAppsOptionId () {
        return this.allAppsOption[0].id;
    }

    getDefaultSessionReplayAppOption () {
        return this.getOptionByAppId(Apps.ALL_PLATFORM);
    }
}

export const AppChooserService = new AppChooser();
